import React from 'react';
import { motion } from 'motion/react';
import { Shield, Lock, Eye, FileText } from 'lucide-react';
import SEO from '@/src/components/SEO';

export default function PrivacyPolicy() {
  return (
    <div className="pt-32 pb-24">
      <SEO 
        title="Privacy Policy"
        description="AGRANDINTL's privacy policy detailing how we collect, use, and protect your personal information."
      />
      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-12"
        >
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-[10px] font-bold uppercase tracking-[0.4em]">
              <Lock className="w-3 h-3" /> Data Protection
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-bold tracking-tighter">Privacy <span className="text-accent">Policy</span></h1>
            <p className="text-muted-foreground text-lg font-light">Last Updated: April 10, 2026</p>
          </div>

          <div className="glass-card rounded-[3rem] p-10 md:p-16 space-y-10 border-white/5">
            <section className="space-y-6">
              <h2 className="text-2xl font-display font-bold flex items-center gap-3">
                <Shield className="w-6 h-6 text-accent" /> 1. Introduction
              </h2>
              <p className="text-muted-foreground leading-relaxed font-light">
                At AGRANDINTL ("we," "us," or "our"), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your data when you visit our website or use our services.
              </p>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-display font-bold flex items-center gap-3">
                <Eye className="w-6 h-6 text-accent" /> 2. Information We Collect
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed font-light">
                <p>We may collect personal information that you voluntarily provide to us when you:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Request a security audit or consultation.</li>
                  <li>Download our resources or reports.</li>
                  <li>Contact us through our website forms.</li>
                  <li>Subscribe to our newsletter.</li>
                </ul>
                <p>This information may include your name, email address, job title, company name, and phone number.</p>
              </div>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-display font-bold flex items-center gap-3">
                <Lock className="w-6 h-6 text-accent" /> 3. How We Use Your Information
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground leading-relaxed font-light">
                <li>To provide and maintain our services.</li>
                <li>To process your requests and provide security reports.</li>
                <li>To communicate with you about our services and updates.</li>
                <li>To improve our website and user experience.</li>
                <li>To comply with legal obligations.</li>
              </ul>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-display font-bold flex items-center gap-3">
                <FileText className="w-6 h-6 text-accent" /> 4. Data Security
              </h2>
              <p className="text-muted-foreground leading-relaxed font-light">
                We implement military-grade security measures to protect your data. All information submitted through our forms is encrypted using SSL (Secure Sockets Layer) technology. We restrict access to your personal information to employees and contractors who need that information to process it on our behalf.
              </p>
            </section>

            <section className="space-y-6">
              <h2 className="text-2xl font-display font-bold flex items-center gap-3">
                <Shield className="w-6 h-6 text-accent" /> 5. Your Rights
              </h2>
              <p className="text-muted-foreground leading-relaxed font-light">
                You have the right to access, correct, or delete your personal information. If you wish to exercise these rights, please contact us at intelligence@agrandintl.com.
              </p>
            </section>

            <section className="space-y-6 pt-10 border-t border-white/5">
              <h2 className="text-2xl font-display font-bold">Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed font-light">
                If you have any questions about this Privacy Policy, please contact our Data Protection Officer at:
              </p>
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <p className="text-foreground font-bold">AGRANDINTL</p>
                <p className="text-sm text-muted-foreground">Email: contact@agrandintl.com</p>
                <p className="text-sm text-muted-foreground">Phone: +1 202 630 6862</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
